/** Wire shapes for the workflows API. Mirrors backend/app/workflows/schemas.py. */

export type ParamKind =
  | 'text'
  | 'expression'
  | 'number'
  | 'boolean'
  | 'select'
  | 'json'
  | 'map'
  | 'code'
  | 'connection'
  | 'action'
  | 'action_input'
  | 'workflow'
  | 'cases'
  | 'fields'
  | 'contract'
  | 'graph'
  | 'node_type'
  | 'edge_type'

export interface ParamSpec {
  key: string
  label: string
  kind: ParamKind
  help: string
  required: boolean
  default: unknown
  options: { value: string; label: string }[]
  placeholder: string
  when: Record<string, unknown[]> | null
}

export interface NodeSpec {
  type: string
  label: string
  group: 'trigger' | 'connector' | 'http' | 'brain' | 'flow' | 'data' | 'output'
  summary: string
  icon: string
  outputs: string[]
  has_input: boolean
  loop_ports: [string, string] | null
  params: ParamSpec[]
}

/** One action a connected app declares, as the picker lists it. */
export interface AppAction {
  id: string
  name: string
  description: string
  category: string | null
  method: string
  is_lookup: boolean
  paginated: boolean
  /** List-shaped: can also be watched as an app event. */
  watchable: boolean
}

/** An app the platform knows: each one is its own family of nodes. */
export interface AppSpec {
  id: string
  name: string
  description: string
  category: string
  logo: string | null
  actions: AppAction[]
}

export interface Catalogue {
  groups: { key: NodeSpec['group']; label: string; summary?: string }[]
  nodes: NodeSpec[]
  apps: AppSpec[]
}

export interface WfNode {
  id: string
  type: string
  name: string
  params: Record<string, unknown>
  position: { x: number; y: number }
  disabled?: boolean
  notes?: string | null
  /** `fail` ends the run; `continue` records the error and passes items on. */
  on_error?: 'fail' | 'continue'
  retries?: number
}

export interface WfEdge {
  id: string
  source: string
  source_port: string
  target: string
}

export interface WorkflowSettings {
  max_steps: number
  max_loop_iterations: number
  timeout_s: number
  keep_runs: number
}

export interface WorkflowIn {
  name: string
  description?: string | null
  active: boolean
  nodes: WfNode[]
  edges: WfEdge[]
  variables: Record<string, unknown>
  settings: WorkflowSettings
  tags: string[]
}

export type RunStatus = 'queued' | 'running' | 'success' | 'error' | 'cancelled'

export interface Workflow extends WorkflowIn {
  workflow_id: string
  user_id: string
  version: number
  created_at: string
  updated_at: string
  trigger_state: {
    webhook?: {
      path: string
      pattern: string | null
      path_params: string[]
      method: string
      secret: string | null
      respond: 'immediately' | 'when_done' | 'no_content'
      query: { name: string; type: string; required: boolean; description: string; example: unknown }[]
      headers: { name: string; type: string; required: boolean; description: string; example: unknown }[]
      body_schema: Record<string, unknown> | null
    }
    schedule?: { mode: string; minutes: number; cron: string; next_run_at: string | null; last_run_at: string | null }
    app?: {
      connection_id: string
      action_id: string
      minutes: number
      key: string
      primed: boolean
      seen_count?: number
      next_run_at: string | null
      last_polled_at: string | null
      last_error: string | null
    }
  }
  last_run_at: string | null
  last_run_status: RunStatus | null
  run_count: number
}

export interface WorkflowSummary {
  workflow_id: string
  name: string
  description: string | null
  active: boolean
  tags: string[]
  node_count: number
  trigger: string | null
  trigger_state: Workflow['trigger_state']
  version: number
  updated_at: string
  created_at: string
  last_run_at: string | null
  last_run_status: RunStatus | null
  run_count: number
}

export interface StepRecord {
  node_id: string
  name: string
  type: string
  status: 'success' | 'error' | 'skipped'
  started_at: string
  duration_ms: number
  input_count: number
  output: Record<string, unknown[]>
  output_counts: Record<string, number>
  error: string | null
  iteration: number | null
}

export interface Run {
  run_id: string
  workflow_id: string
  workflow_name: string
  trigger: 'manual' | 'webhook' | 'schedule' | 'workflow' | 'app'
  status: RunStatus
  started_at: string
  finished_at: string | null
  duration_ms: number
  input: unknown
  output: unknown
  error: string | null
  steps: StepRecord[]
  step_count: number
  logs?: string[]
  /** Set when the run was a step test: the node it stopped at. */
  stopped_at?: string | null
}

export interface RunBody {
  input?: unknown
  draft?: WorkflowIn
  /** Stop after this node — the editor's "test this step". */
  until_node_id?: string
}

export interface RunSummary {
  run_id: string
  workflow_id: string
  workflow_name: string
  trigger: Run['trigger']
  status: RunStatus
  started_at: string
  finished_at: string | null
  duration_ms: number
  step_count: number
  error: string | null
}

export const DEFAULT_SETTINGS: WorkflowSettings = {
  max_steps: 2000,
  max_loop_iterations: 1000,
  timeout_s: 300,
  keep_runs: 200,
}
